import { IMenuDTO, IMenuItemDTO, IMenuStylesDTO, Menu } from '../controls/menu/Menu';
import { GetPixelsFromRem } from '@shared/frontend';
import { createContext, ReactNode, RefObject, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

/** Menu context type definition. */
interface MenuContextType {
  menuProps: IMenuDTO | null;
  setMenuProps: (props: IMenuDTO | null) => void;
  showMenu: (
    ref: RefObject<HTMLDivElement | null> | null,
    items: IMenuItemDTO[],
    menuStyles: IMenuStylesDTO,
    rtl?: boolean,
    xOffsetREM?: number,
    yOffsetREM?: number,
    xSubOffsetREM?: number,
    ySubOffsetREM?: number
  ) => void;
  hideMenu: () => void;
}

/** Menu context instance. */
const MenuContext = createContext<MenuContextType | undefined>(undefined);

/**
 * Menu provider component.
 *
 * @param param0 - The child components to be wrapped by the MenuProvider.
 * @param param0.children
 * @returns A JSX element wrapping children with MenuContext provider.
 */
export const MenuProvider = ({ children }: { children: ReactNode }) => {
  const [menuProps, setMenuProps] = useState<IMenuDTO | null>(null);
  const mousePos = useMousePosition();

  // Show menu function
  const showMenu = useCallback(
    (
      ref: RefObject<HTMLDivElement | null> | null,
      items: IMenuItemDTO[],
      menuStyles: IMenuStylesDTO,
      rtl?: boolean,
      xOffsetREM: number = 0,
      yOffsetREM: number = 0,
      xSubOffsetREM: number = 0,
      ySubOffsetREM: number = 0
    ) => {
      setMenuProps({
        ref,
        items,
        menuStyles,
        rtl,
        x: mousePos.current.x - GetPixelsFromRem(xOffsetREM),
        y: mousePos.current.y - GetPixelsFromRem(yOffsetREM),
        xSub: GetPixelsFromRem(xSubOffsetREM),
        ySub: GetPixelsFromRem(ySubOffsetREM),
        action: () => setMenuProps(null) // Close on menu action (item action will still take effect)
      });
    },
    [setMenuProps, mousePos]
  );

  // Hide menu function
  const hideMenu = useCallback(() => {
    setMenuProps(null);
  }, [setMenuProps]);

  // Memoized context value
  const value = useMemo(
    () => ({
      menuProps,
      setMenuProps,
      showMenu,
      hideMenu
    }),
    [menuProps, setMenuProps, showMenu, hideMenu]
  );

  // Render provider
  return (
    <MenuContext.Provider value={value}>
      {children}
      {menuProps && <Menu {...menuProps} />}
    </MenuContext.Provider>
  );
};

/**
 * Custom hook to access menu context.
 *
 * @returns The menu context value.
 * @throws Error if used outside of MenuProvider.
 */
export function useMenu(): MenuContextType {
  const context = useContext(MenuContext);
  if (!context) {
    throw new Error('useMenu must be used within a MenuProvider');
  }
  return context;
}

/**
 * Custom hook to track mouse position.
 *
 * @returns A ref object containing the current mouse x and y coordinates.
 */
export function useMousePosition() {
  const pos = useRef({ x: 0, y: 0 });
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      pos.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('mousemove', handler);
    return () => window.removeEventListener('mousemove', handler);
  }, []);
  return pos;
}
